import { Component } from '@theme/component';
import { normalizeString } from '@theme/utilities';

/**
 * @typedef {{
 *   title?: string;
 *   handle?: string;
 *   icon?: string;
 *   badge?: string;
 *   description?: string;
 *   view_all_label?: string;
 *   view_all_url?: string;
 *   featured_cards?: RawCard[];
 *   image_entries?: RawImageEntry[];
 * }} RawCategory
 *
 * @typedef {{
 *   title?: string;
 *   subtitle?: string;
 *   badge?: string;
 *   url?: string;
 *   image_url?: string;
 *   image_alt?: string;
 * }} RawCard
 *
 * @typedef {{
 *   title?: string;
 *   badge?: string;
 *   url?: string;
 *   image_url?: string;
 *   image_alt?: string;
 * }} RawImageEntry
 *
 * @typedef {{
 *   link?: { title?: string; handle?: string; url?: string };
 *   source?: string;
 *   panel?: {
 *     eyebrow?: string;
 *     category_heading?: string;
 *     room_heading?: string;
 *     view_all_label?: string;
 *     view_all_url?: string;
 *     room_view_all_label?: string;
 *     room_view_all_url?: string;
 *     empty_title?: string;
 *     empty_copy?: string;
 *     categories?: RawCategory[];
 *     room_entries?: RawImageEntry[];
 *   };
 * }} RawPanelData
 */

/**
 * @typedef {{
 *   id: string;
 *   title: string;
 *   subtitle: string;
 *   badge: string;
 *   url: string;
 *   image: { src: string; alt: string };
 * }} ProductCard
 *
 * @typedef {{
 *   id: string;
 *   title: string;
 *   badge: string;
 *   url: string;
 *   image: { src: string; alt: string };
 * }} ImageEntryCard
 *
 * @typedef {{
 *   id: string;
 *   title: string;
 *   icon: string;
 *   badge: string;
 *   description: string;
 *   viewAllLabel: string;
 *   viewAllUrl: string;
 *   cards: ProductCard[];
 *   imageEntries: ImageEntryCard[];
 * }} Category
 *
 * @typedef {{
 *   state: 'ready' | 'empty' | 'error';
 *   id: string;
 *   title: string;
 *   eyebrow: string;
 *   categoryHeading: string;
 *   roomHeading: string;
 *   viewAllLabel: string;
 *   viewAllUrl: string;
 *   roomViewAllLabel: string;
 *   roomViewAllUrl: string;
 *   emptyTitle: string;
 *   emptyCopy: string;
 *   categories: Category[];
 *   roomEntries: ImageEntryCard[];
 * }} MegaMenuPanel
 */

const DEFAULT_EYEBROW = 'Curated Navigation';
const DEFAULT_CATEGORY_HEADING = 'Shop By Category';
const DEFAULT_ROOM_HEADING = 'Shop By Room';
const DEFAULT_VIEW_ALL = 'View All >';
const MEGA_TEXT_FIX_STYLE_ID = 'sibrille-govee-mega-text-fix-20260610';
const MEGA_TEXT_FIX_CSS = `
@media screen and (min-width: 990px) {
  body #header-component .header-menu[data-menu-variant='govee-mega'] .govee-mega-menu__surface,
  body #header-component .header-menu[data-menu-variant='govee-mega'] .govee-mega-menu__state {
    grid-template-columns: 244px minmax(560px, 620px) 300px !important;
    gap: clamp(44px, 3.2vw, 52px) !important;
  }

  body #header-component .header-menu[data-menu-variant='govee-mega'] .govee-mega-menu__feature-rail {
    max-width: 620px !important;
  }

  body #header-component .header-menu[data-menu-variant='govee-mega'] .govee-mega-menu__category-trigger {
    overflow: visible !important;
  }

  body #header-component .header-menu[data-menu-variant='govee-mega'] .govee-mega-menu__category-content,
  body #header-component .header-menu[data-menu-variant='govee-mega'] .govee-mega-menu__category-title-row {
    width: 100% !important;
    min-width: 0 !important;
  }

  body #header-component .header-menu[data-menu-variant='govee-mega'] .govee-mega-menu__category-title-row {
    gap: 6px !important;
  }

  body #header-component .header-menu[data-menu-variant='govee-mega'] .govee-mega-menu__category-title {
    flex: 1 1 auto !important;
    min-width: 0 !important;
    white-space: nowrap !important;
  }

  body #header-component .header-menu[data-menu-variant='govee-mega'] .govee-mega-menu__badge {
    flex: 0 0 auto !important;
    padding-inline: 8px !important;
  }

  body #header-component .header-menu[data-menu-variant='govee-mega'] .govee-mega-menu__cards-grid {
    gap: 36px 36px !important;
  }

  body #header-component .header-menu[data-menu-variant='govee-mega'] .govee-product-card {
    min-height: 96px !important;
  }

  body #header-component .header-menu[data-menu-variant='govee-mega'] .govee-product-card__body,
  body #header-component .header-menu[data-menu-variant='govee-mega'] .govee-product-card__title-row,
  body #header-component .header-menu[data-menu-variant='govee-mega'] .govee-product-card__title,
  body #header-component .header-menu[data-menu-variant='govee-mega'] .govee-product-card__subtitle {
    min-width: 0 !important;
  }

  body #header-component .header-menu[data-menu-variant='govee-mega'] .govee-product-card__title,
  body #header-component .header-menu[data-menu-variant='govee-mega'] .govee-product-card__subtitle {
    white-space: normal !important;
    overflow-wrap: anywhere !important;
    -webkit-line-clamp: 2 !important;
  }

  body #header-component .header-menu[data-menu-variant='govee-mega'] .govee-mega-menu__room-rail {
    max-width: 300px !important;
  }

  body #header-component .header-menu[data-menu-variant='govee-mega'] .govee-room-card__visual,
  body #header-component .header-menu[data-menu-variant='govee-mega'] .govee-mega-menu__feature-card,
  body #header-component .header-menu[data-menu-variant='govee-mega'] .govee-mega-menu__feature-card-visual {
    width: 300px !important;
  }
}`;

function injectMegaTextFixStyles() {
  if (typeof document === 'undefined' || document.getElementById(MEGA_TEXT_FIX_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = MEGA_TEXT_FIX_STYLE_ID;
  style.textContent = MEGA_TEXT_FIX_CSS;
  document.head.appendChild(style);
}

class GoveeMegaMenuPanel extends Component {
  /** @type {MegaMenuPanel | null} */
  #panel = null;

  /** @type {number | null} */
  #renderTimer = null;

  /** @type {string | null} */
  #sourceSnapshot = null;

  connectedCallback() {
    super.connectedCallback();
    injectMegaTextFixStyles();
    this.#queueRender();
  }

  updatedCallback() {
    super.updatedCallback();
    const source = this.querySelector('[data-govee-mega-menu-source]');
    const snapshot = source instanceof HTMLScriptElement ? source.textContent || '' : null;
    const mount = this.refs.mount || this.querySelector('[ref="mount"]');

    if (
      this.#panel &&
      snapshot === this.#sourceSnapshot &&
      mount instanceof HTMLElement &&
      mount.innerHTML.trim() !== ''
    ) {
      return;
    }

    this.#queueRender();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#renderTimer !== null) {
      clearTimeout(this.#renderTimer);
      this.#renderTimer = null;
    }
  }

  #queueRender() {
    if (this.#renderTimer !== null) {
      clearTimeout(this.#renderTimer);
    }

    const mount = this.refs.mount || this.querySelector('[ref="mount"]');
    const source = this.querySelector('[data-govee-mega-menu-source]');

    if (mount instanceof HTMLElement && source instanceof HTMLScriptElement) {
      this.#render();
      return;
    }

    // Defer the initial render so refs and inline JSON are present after the
    // custom element upgrades inside Shopify's server-rendered mega menu markup.
    this.#renderTimer = window.setTimeout(() => {
      this.#renderTimer = null;
      this.#render();
    }, 0);
  }

  #render() {
    const mount = this.refs.mount || this.querySelector('[ref="mount"]');
    const source = this.querySelector('[data-govee-mega-menu-source]');
    if (!(mount instanceof HTMLElement) || !(source instanceof HTMLScriptElement)) return;
    this.#sourceSnapshot = source.textContent || '';

    try {
      const raw = /** @type {RawPanelData} */ (JSON.parse(this.#sourceSnapshot || '{}'));
      this.#panel = adaptMegaMenuPanel(raw);
    } catch (error) {
      console.error('Unable to parse mega menu payload', error);
      this.#panel = {
        state: 'error',
        id: this.dataset.linkHandle || 'mega-menu',
        title: this.dataset.linkTitle || 'Mega Menu',
        eyebrow: DEFAULT_EYEBROW,
        categoryHeading: DEFAULT_CATEGORY_HEADING,
        roomHeading: DEFAULT_ROOM_HEADING,
        viewAllLabel: DEFAULT_VIEW_ALL,
        viewAllUrl: '#',
        emptyTitle: 'This mega menu could not be rendered.',
        emptyCopy: 'Check the linked Shopify navigation item and metaobject payload.',
        categories: [],
      };
    }

    mount.innerHTML = renderMegaMenuPanel(this.#panel);
    this.#attachImageFallbacks(mount);
    this.#bindCategorySwitching(mount);
  }

  /**
   * @param {HTMLElement} mount
   */
  #attachImageFallbacks(mount) {
    mount.querySelectorAll('[data-media-card]').forEach((card) => {
      const image = card.querySelector('img');
      if (!(image instanceof HTMLImageElement)) {
        card.setAttribute('data-image-failed', 'true');
        return;
      }

      const fail = () => {
        card.setAttribute('data-image-failed', 'true');
      };

      if (!image.getAttribute('src')) {
        fail();
        return;
      }

      image.addEventListener('error', fail, { once: true });
    });
  }

  /**
   * @param {HTMLElement} mount
   */
  #bindCategorySwitching(mount) {
    const root = mount.querySelector('[data-govee-panel-root]');
    if (!(root instanceof HTMLElement)) return;

    const buttons = [...root.querySelectorAll('[data-category-trigger]')];
    const panels = [...root.querySelectorAll('[data-category-panel]')];

    if (!buttons.length || !panels.length) return;

    const setActive = (id) => {
      buttons.forEach((button) => {
        const isActive = button.getAttribute('data-category-trigger') === id;
        button.setAttribute('aria-selected', String(isActive));
        button.tabIndex = isActive ? 0 : -1;
        button.toggleAttribute('data-active', isActive);
      });

      panels.forEach((panel) => {
        const isActive = panel.getAttribute('data-category-panel') === id;
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('aria-hidden', String(!isActive));
      });
    };

    buttons.forEach((button) => {
      const activate = () => setActive(button.getAttribute('data-category-trigger') || '');
      button.addEventListener('pointerenter', activate);
      button.addEventListener('focus', activate);
      button.addEventListener('click', activate);
    });

    const initial = buttons.find((button) => button.hasAttribute('data-active')) || buttons[0];
    setActive(initial.getAttribute('data-category-trigger') || '');
  }
}

if (!customElements.get('govee-mega-menu-panel')) {
  customElements.define('govee-mega-menu-panel', GoveeMegaMenuPanel);
}

/**
 * @param {RawPanelData} raw
 * @returns {MegaMenuPanel}
 */
function adaptMegaMenuPanel(raw) {
  const link = raw?.link || {};
  const rawPanel = raw?.panel || {};

  const id = link.handle || normalizeString(link.title || 'mega-menu');
  const categories = (rawPanel.categories || []).map(adaptCategory).filter(Boolean);

  const panel = {
    state: 'ready',
    id,
    title: link.title || 'Mega Menu',
    eyebrow: rawPanel.eyebrow || DEFAULT_EYEBROW,
    categoryHeading: rawPanel.category_heading || DEFAULT_CATEGORY_HEADING,
    roomHeading: rawPanel.room_heading || DEFAULT_ROOM_HEADING,
    viewAllLabel: rawPanel.view_all_label || DEFAULT_VIEW_ALL,
    viewAllUrl: rawPanel.view_all_url || link.url || '#',
    roomViewAllLabel: rawPanel.room_view_all_label || DEFAULT_VIEW_ALL,
    roomViewAllUrl: rawPanel.room_view_all_url || link.url || '#',
    emptyTitle: rawPanel.empty_title || `${link.title || 'Navigation'} is not configured yet.`,
    emptyCopy:
      rawPanel.empty_copy ||
      'Add category content through Shopify navigation or linked metaobjects to populate this mega menu.',
    categories,
    roomEntries: (rawPanel.room_entries || []).map(adaptImageEntry).filter(Boolean),
  };

  if (!link.handle) {
    return { ...panel, state: 'error', emptyTitle: 'Navigation link metadata is missing.' };
  }

  if (!categories.length) {
    return { ...panel, state: 'empty' };
  }

  return panel;
}

/**
 * @param {RawCategory} rawCategory
 * @returns {Category | null}
 */
function adaptCategory(rawCategory) {
  if (!rawCategory) return null;

  const title = (rawCategory.title || '').trim();
  const id = rawCategory.handle || normalizeString(title);
  if (!title || !id) return null;

  return {
    id,
    title,
    icon: (rawCategory.icon || '').trim(),
    badge: (rawCategory.badge || '').trim(),
    description: (rawCategory.description || '').trim(),
    viewAllLabel: (rawCategory.view_all_label || DEFAULT_VIEW_ALL).trim(),
    viewAllUrl: (rawCategory.view_all_url || '#').trim(),
    cards: (rawCategory.featured_cards || []).map(adaptCard).filter(Boolean),
    imageEntries: [],
  };
}

/**
 * @param {RawCard} rawCard
 * @returns {ProductCard | null}
 */
function adaptCard(rawCard) {
  const title = (rawCard?.title || '').trim();
  const url = (rawCard?.url || '#').trim();
  if (!title) return null;

  return {
    id: `${normalizeString(title)}-${normalizeString(url)}`,
    title,
    subtitle: (rawCard?.subtitle || '').trim(),
    badge: (rawCard?.badge || '').trim(),
    url,
    image: {
      src: (rawCard?.image_url || '').trim(),
      alt: (rawCard?.image_alt || title).trim(),
    },
  };
}

/**
 * @param {RawImageEntry} rawEntry
 * @returns {ImageEntryCard | null}
 */
function adaptImageEntry(rawEntry) {
  const title = (rawEntry?.title || '').trim();
  const url = (rawEntry?.url || '#').trim();
  if (!title) return null;

  return {
    id: `${normalizeString(title)}-${normalizeString(url)}`,
    title,
    badge: (rawEntry?.badge || '').trim(),
    url,
    image: {
      src: (rawEntry?.image_url || '').trim(),
      alt: (rawEntry?.image_alt || title).trim(),
    },
  };
}

/**
 * @param {MegaMenuPanel} panel
 * @returns {string}
 */
function renderMegaMenuPanel(panel) {
  if (panel.state === 'error') {
    return renderState(panel.emptyTitle, panel.emptyCopy, 'error');
  }

  if (panel.state === 'empty') {
    return renderState(panel.emptyTitle, panel.emptyCopy, 'empty');
  }

  const categories = panel.categories
    .map((category, index) => renderCategoryTrigger(category, index === 0))
    .join('');

  const panels = panel.categories.map((category, index) => renderCategoryPanel(category, index === 0)).join('');

  const roomRail = renderRoomRail(panel);

  return `
    <div class="govee-mega-menu" data-govee-panel-root>
      <div class="govee-mega-menu__surface">
        <aside class="govee-mega-menu__sidebar">
          <h3 class="govee-mega-menu__title">${escapeHtml(panel.categoryHeading)}</h3>
          <div class="govee-mega-menu__category-list" role="tablist" aria-label="${escapeAttribute(panel.categoryHeading)}">
            ${categories}
          </div>
          <a class="govee-mega-menu__view-all govee-mega-menu__view-all--sidebar" href="${escapeAttribute(panel.viewAllUrl)}">
            ${escapeHtml(panel.viewAllLabel)}
          </a>
        </aside>
        <div class="govee-mega-menu__content-stage">
          ${panels}
        </div>
        ${roomRail}
      </div>
    </div>
  `;
}

/**
 * @param {Category} category
 * @param {boolean} active
 * @returns {string}
 */
function renderCategoryTrigger(category, active) {
  return `
    <button
      type="button"
      class="govee-mega-menu__category-trigger"
      data-category-trigger="${escapeAttribute(category.id)}"
      ${active ? 'data-active' : ''}
      aria-selected="${active}"
      role="tab"
      tabindex="${active ? '0' : '-1'}"
    >
      <span class="govee-mega-menu__category-content">
        <span class="govee-mega-menu__category-title-row">
          ${category.icon ? `<span class="govee-mega-menu__category-icon">${escapeHtml(category.icon)}</span>` : ''}
          <span class="govee-mega-menu__category-title">${escapeHtml(category.title)}</span>
          ${category.badge ? `<span class="govee-mega-menu__badge">${escapeHtml(category.badge)}</span>` : ''}
        </span>
      </span>
    </button>
  `;
}

/**
 * @param {Category} category
 * @param {boolean} active
 * @returns {string}
 */
function renderCategoryPanel(category, active) {
  const cards = category.cards.slice(0, 4);

  return `
    <div
      class="govee-mega-menu__content-panel ${active ? 'is-active' : ''}"
      data-category-panel="${escapeAttribute(category.id)}"
      aria-hidden="${!active}"
    >
      <section class="govee-mega-menu__feature-rail">
        <p class="govee-mega-menu__section-label govee-mega-menu__section-label--subtle">Recommended</p>
        ${
          cards.length
            ? `<div class="govee-mega-menu__cards-grid">${cards.map(renderProductCard).join('')}</div>`
            : renderInlineEmpty('No featured cards yet', 'Add curated products or collection links for this category.')
        }
        <a class="govee-mega-menu__section-link govee-mega-menu__section-link--bottom" href="${escapeAttribute(category.viewAllUrl)}">
          ${escapeHtml(category.viewAllLabel)}
        </a>
      </section>
    </div>
  `;
}

/**
 * @param {MegaMenuPanel} panel
 * @returns {string}
 */
function renderRoomRail(panel) {
  const imageEntries = panel.roomEntries.slice(0, 6);

  return `
    <section class="govee-mega-menu__room-rail">
      <div class="govee-mega-menu__section-header">
        <h4 class="govee-mega-menu__section-title">${escapeHtml(panel.roomHeading)}</h4>
      </div>
      ${
        imageEntries.length
          ? `<div class="govee-mega-menu__image-grid">${imageEntries.map(renderImageEntryCard).join('')}</div>`
          : renderInlineEmpty('No visual entry cards yet', 'Bind a room panel object or add a linked rooms menu for this navigation item.')
      }
    </section>
  `;
}

/**
 * @param {ProductCard} card
 * @returns {string}
 */
function renderProductCard(card) {
  return `
    <a class="govee-product-card govee-media-card" href="${escapeAttribute(card.url)}" data-media-card>
      <span class="govee-product-card__media">
        <span class="govee-product-card__media-frame">
          ${card.image.src ? `<img class="govee-media-card__image" src="${escapeAttribute(card.image.src)}" alt="${escapeAttribute(card.image.alt)}" loading="lazy">` : ''}
          <span class="govee-media-card__fallback">${escapeHtml(card.title)}</span>
        </span>
      </span>
      <span class="govee-product-card__body">
        <span class="govee-product-card__title-row">
          ${card.badge ? `<span class="govee-mega-menu__badge govee-mega-menu__badge--inline">${escapeHtml(card.badge)}</span>` : ''}
          <span class="govee-product-card__title">${escapeHtml(card.title)}</span>
        </span>
        ${card.subtitle ? `<span class="govee-product-card__subtitle">${escapeHtml(card.subtitle)}</span>` : ''}
      </span>
    </a>
  `;
}

/**
 * @param {ImageEntryCard} entry
 * @returns {string}
 */
function renderImageEntryCard(entry) {
  return `
    <a class="govee-room-card govee-media-card" href="${escapeAttribute(entry.url)}" data-media-card>
      <span class="govee-room-card__visual">
        ${entry.image.src ? `<img class="govee-media-card__image" src="${escapeAttribute(entry.image.src)}" alt="${escapeAttribute(entry.image.alt)}" loading="lazy">` : ''}
        <span class="govee-media-card__fallback">${escapeHtml(entry.title)}</span>
        <span class="govee-room-card__overlay"></span>
      </span>
      <span class="govee-room-card__caption">
        <span class="govee-room-card__title">${escapeHtml(entry.title)}</span>
      </span>
    </a>
  `;
}

/**
 * @param {string} title
 * @param {string} copy
 * @param {'empty' | 'error'} tone
 * @returns {string}
 */
function renderState(title, copy, tone) {
  return `
    <div class="govee-mega-menu__state govee-mega-menu__state--${escapeAttribute(tone)}">
      <p class="govee-mega-menu__state-kicker">${tone === 'error' ? 'Config error' : 'No data yet'}</p>
      <h3 class="govee-mega-menu__state-title">${escapeHtml(title)}</h3>
      <p class="govee-mega-menu__state-copy">${escapeHtml(copy)}</p>
    </div>
  `;
}

/**
 * @param {string} title
 * @param {string} copy
 * @returns {string}
 */
function renderInlineEmpty(title, copy) {
  return `
    <div class="govee-mega-menu__inline-empty">
      <p class="govee-mega-menu__inline-empty-title">${escapeHtml(title)}</p>
      <p class="govee-mega-menu__inline-empty-copy">${escapeHtml(copy)}</p>
    </div>
  `;
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeAttribute(value) {
  return escapeHtml(value || '');
}
